// Shared utility functions for SmartReqs frontend
import { EDUCATION_CONFIG } from "@/components/EducationRequirements";

/**
 * Convert structured education object to display string
 * @param {Object|string} education - Education data (structured object or string)
 * @returns {string} Formatted education display string
 */
export const formatEducationForDisplay = (education) => {
  if (!education) return "";
  if (typeof education === "string") return education;
  
  if (education.track === "general_academic") {
    const getDegreeLabel = (degreeValue) => {
      return EDUCATION_CONFIG.general_academic.higherDegrees.find(d => d.value === degreeValue)?.label || degreeValue;
    };
    
    // Get all unique fields across required and preferred
    const reqFields = education.required?.fields || [];
    const prefFields = education.preferred?.fields || [];
    const allUniqueFields = [...new Set([...reqFields, ...prefFields])];
    
    // Build descriptions for all unique fields (shown once at end)
    const allDescriptions = allUniqueFields
      .filter(f => f !== "related_field")
      .map(f => {
        const field = EDUCATION_CONFIG.general_academic.fields.find(field => field.value === f);
        return field?.description || "";
      })
      .filter(d => d);
    
    // Format degree with field labels - use "or" for Related Field
    const formatDegreeBase = (degreeValue, fields) => {
      const degreeLabel = getDegreeLabel(degreeValue);
      
      // Separate primary fields from "Related Field"
      const primaryFields = fields.filter(f => f !== "related_field");
      const hasRelatedField = fields.includes("related_field");
      
      const primaryLabels = primaryFields.map(f => {
        const field = EDUCATION_CONFIG.general_academic.fields.find(field => field.value === f);
        return field?.label || f;
      });
      
      // Join with "or Related Field" if present
      let fieldText = primaryLabels.join(", ");
      if (hasRelatedField) {
        fieldText += " or Related Field";
      }
      
      return `${degreeLabel} in ${fieldText}`;
    };
    
    const parts = [];
    
    // Required
    const requiredBases = [];
    if (education.required?.hsGed) {
      requiredBases.push("High School/GED");
    }
    if (education.required?.degree && education.required.degree !== "none" && reqFields.length > 0) {
      requiredBases.push(formatDegreeBase(education.required.degree, reqFields));
    }
    if (requiredBases.length > 0) {
      parts.push(requiredBases.join(" + ") + " required");
    }
    
    // Preferred
    if (education.preferred?.degree && education.preferred.degree !== "none" && prefFields.length > 0) {
      parts.push(formatDegreeBase(education.preferred.degree, prefFields) + " preferred");
    }
    
    // Join parts and add all descriptions ONCE at the end
    let result = parts.join("; ");
    if (allDescriptions.length > 0) {
      result += ` (${allDescriptions.join("; ")})`;
    }
    
    return result;
  } else if (education.track === "healthcare_clinical") {
    const getCredLabel = (credKey) => {
      const [catKey, credValue] = credKey.split(":");
      const category = EDUCATION_CONFIG.healthcare_clinical.categories[catKey];
      const cred = category?.credentials.find(c => c.value === credValue);
      return cred?.label || credValue;
    };
    
    // Healthcare/Clinical track
    const requiredParts = [];
    
    // Add HS/GED if selected
    if (education.required?.hsGed) {
      requiredParts.push("High School/GED");
    }
    
    // Add credentials
    if (education.required?.credentials?.length > 0) {
      requiredParts.push(education.required.credentials.map(getCredLabel).join(", "));
    }
    
    const parts = [];
    if (requiredParts.length > 0) {
      parts.push(requiredParts.join(" + ") + " required");
    }
    
    if (education.preferred?.credentials?.length > 0) {
      parts.push(education.preferred.credentials.map(getCredLabel).join(", ") + " preferred");
    }
    return parts.join("; ");
  }
  
  return "";
};

/**
 * Get field descriptions from education object
 * @param {Object} education - Education data object
 * @returns {Array<string>} Array of field descriptions
 */
export const getEducationFieldDescriptions = (education) => {
  if (!education || education.track !== "general_academic") return [];
  
  const descriptions = [];
  const allFields = new Set([
    ...(education.required?.fields || []),
    ...(education.preferred?.fields || [])
  ]);
  
  allFields.forEach(f => {
    if (f !== "related_field") {
      const field = EDUCATION_CONFIG.general_academic.fields.find(field => field.value === f);
      if (field?.description) {
        descriptions.push(field.description);
      }
    }
  });
  
  return descriptions;
};
